import { resourceValidationIds } from './resource-validation-ids';
import { walkLayers, type EditorDocument, type ImageLayer } from '../core/model';
import { throwIfAborted } from '../codecs/types';
import { documentPublicResources } from './adapter';
import { prepareDraftSceneImages, type PreparedDraftImages } from './image-assets';

interface ImageState { resources: PreparedDraftImages; owners: number; bytes: number }
export type NativeImageState = ImageState | undefined;
const prepared = new WeakMap<EditorDocument, ImageState>();
function retain(state: NativeImageState): void {
  if (state) { if (state.owners <= 0) throw new Error('Image state has been released.'); state.owners++; }
}
function drop(state: NativeImageState): void {
  if (state && --state.owners === 0) state.resources.dispose();
}
/** A history entry owns this lease independently of the active document. */
export function retainNativeImageState(document: EditorDocument): { state: NativeImageState; bytes: number; release(): void } {
  const state = prepared.get(document); retain(state); let released = false;
  return { state, bytes: state?.bytes ?? 0, release: () => { if (!released) { released = true; drop(state); } } };
}
export function activateNativeImageState(document: EditorDocument, state: NativeImageState): void {
  const previous = prepared.get(document);
  if (previous === state) return;
  retain(state);
  generations.set(document, (generations.get(document) ?? 0) + 1);
  if (state) prepared.set(document, state); else prepared.delete(document);
  drop(previous);
}
const generations = new WeakMap<EditorDocument, number>();
const renderSources = new WeakMap<EditorDocument, EditorDocument>();

/** Export projections borrow image resources; only the original document owns them. */
export function inheritNativeDocumentImages(source: EditorDocument, projection: EditorDocument): void {
  renderSources.set(projection, source);
}

export function releaseNativeDocumentImages(document: EditorDocument): void {
  generations.set(document, (generations.get(document) ?? 0) + 1);
  drop(prepared.get(document)); prepared.delete(document); renderSources.delete(document);
}

/** Build and validate a replacement before releasing live image resources. */
export async function prepareNativeDocumentImages(document: EditorDocument, signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  const layers = walkLayers(document.root).filter((layer): layer is ImageLayer => layer.kind === 'image');
  if (!layers.length) { releaseNativeDocumentImages(document); return; }
  const metadata = document.extensions['maipo:public-document'];
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)
    || (metadata as Record<string, unknown>).format !== 'maipo-scene'
    || (metadata as Record<string, unknown>).version !== '0.1') throw new Error('Draft image document has no supported retained resource table.');
  const generation = (generations.get(document) ?? 0) + 1;
  generations.set(document, generation);
  const binding = () => JSON.stringify({ metadata: document.extensions['maipo:public-document'], images: walkLayers(document.root).filter(layer => layer.kind === 'image').map(layer => ({ id: layer.id, image: layer.image })) });
  const before = binding();
  const resources = documentPublicResources(document);
  const declarations = (metadata as Record<string, unknown>).extensionDeclarations;
  const retainedDecodedBytes = walkLayers(document.root).reduce((sum, layer) => sum
    + (layer.kind === 'raster' ? layer.raster.byteLength() : 0) + (layer.mask?.tiles.byteLength() ?? 0), document.root.mask?.tiles.byteLength() ?? 0);
  const validationId = resourceValidationIds(resources.fonts, resources.assets);
  const next = await prepareDraftSceneImages({
    format: 'maipo-scene', version: '0.1', documentId: validationId(), revision: 0, name: 'Native resources',
    canvas: { width: 1, height: 1, colorSpace: 'srgb', background: { type: 'transparent' } },
    ...resources,
    children: layers.map(layer => ({ type: 'image', id: validationId(), name: 'Image resource', asset: layer.image.asset, frame: layer.image.frame })),
    ...(declarations === undefined ? {} : { extensionDeclarations: declarations }),
  }, signal, retainedDecodedBytes);
  try {
    throwIfAborted(signal);
    if (generations.get(document) !== generation || before !== binding()) throw new Error('Image bindings changed while resources were prepared.');
  } catch (error) { next.dispose(); throw error; }
  releaseNativeDocumentImages(document);
  const bytes = [...new Set(next.images.values())].reduce((sum, image) => sum + image.width * image.height * 4, 0);
  prepared.set(document, { resources: next, owners: 1, bytes });
}

export function nativeDocumentImageGeneration(document: EditorDocument): number {
  return generations.get(renderSources.get(document) ?? document) ?? 0;
}

export function nativeDocumentImage(document: EditorDocument | undefined, layer: ImageLayer): ImageBitmap {
  const image = document && prepared.get(renderSources.get(document) ?? document)?.resources.images.get(layer.image.asset);
  if (!image) throw new Error(`Image resource ${layer.image.asset} has not been prepared.`);
  return image;
}
